import clsx from "clsx";
import { graphql } from "gatsby";
import React, { FC, useCallback } from "react";

import { useAuthContext } from "../../../auth/auth-context";
import { useDataLayer } from "../../../hooks/use-data-layer";
import { CommonColors } from "../../../types/colors";
import { GoogleAnalyticsEvent } from "../../../utils/analytics";
import { Cta } from "../../buttons/cta";
import { HeroBanner } from "../../strips/hero-banner";
import { HeroBannerVariants, JustifyText, MediaLayoutValues } from "../../strips/hero-banner/types";
import { ProductStripCard } from "./product-strip-card";
import { ProductStripCatalogCta } from "./product-strip-catalog-cta";

export interface ProductStripProps extends Queries.ProductStripDataFragment {
  labels: Record<string, any>;
}

export const ProductStrip: FC<ProductStripProps> = ({
  name,
  defaultLocaleEntry,
  bannerMedia,
  mediaLayout,
  catalog,
  demoCta,
  labels,
}) => {
  const { logged, checking, login } = useAuthContext();
  const { pushEvent } = useDataLayer();
  const onDownloadCatalog = useCallback(() => {
    if (defaultLocaleEntry) {
      pushEvent(
        {
          event: GoogleAnalyticsEvent.DownloadCatalogue,
          catalogue_name: defaultLocaleEntry.name,
          product_name: defaultLocaleEntry.name,
          product_id: defaultLocaleEntry.id,
          product_category: defaultLocaleEntry.specialization?.name ?? null,
          product_category2: defaultLocaleEntry.specialization?.machiningType?.name ?? null,
          product_category3: defaultLocaleEntry.specialization?.machiningType?.material ?? null,
        },
        ["product_id"]
      );
    }
  }, [pushEvent, defaultLocaleEntry]);
  return (
    <>
      {name && (
        <div className="text-primary text-4xl lg:text-7xl leading-snug lg:leading-snug bg-neutral-light px-8 lg:px-20 pt-14">
          {name}
        </div>
      )}
      <HeroBanner
        title={null}
        tagVariant={HeroBannerVariants.h1}
        media={bannerMedia}
        abstract={null}
        showBiesseLogo={false}
        color={CommonColors.NeutralLight}
        padded={true}
        justifyText={JustifyText.CENTER}
        card={null}
        anchors={null}
        forceAspectRatio={true}
        className="pt-2 lg:pt-4"
        mediaFit={mediaLayout === MediaLayoutValues.CONTAIN ? undefined : "cover"}
        isProduct={true}
      >
        {(catalog?.url || demoCta) && (
          <div
            className={clsx(
              "w-full mt-3 lg:-mt-10 z-20 lg:px-16",
              "flex gap-5 items-stretch justify-center flex-col lg:flex-row"
            )}
          >
            {catalog?.url && (
              <ProductStripCard
                title={labels["product-download-catalog-card-title"]}
                description={labels["product-download-catalog-card-description"]}
                cta={
                  <ProductStripCatalogCta
                    catalogUrl={catalog.url}
                    checking={checking}
                    labels={labels}
                    logged={logged}
                    onDownloadCatalog={onDownloadCatalog}
                    onLogin={login}
                  />
                }
                variant="light"
                className="shadow-lg basis-1/2"
              />
            )}
            {demoCta && (
              <ProductStripCard
                title={labels["product-ask-for-demo-card-title"]}
                description={labels["product-ask-for-demo-card-description"]}
                cta={demoCta && <Cta variant="light" {...demoCta} />}
                variant="primary"
                className="shadow-lg basis-1/2"
              />
            )}
          </div>
        )}
      </HeroBanner>
    </>
  );
};

export const query = graphql`
  fragment ProductStripData on ContentfulProduct {
    name
    bannerMedia {
      mimeType
      gatsbyImageData
      placeholderUrl
      url
    }
    mediaLayout
    demoCta {
      ...CtaData
    }
    catalog {
      url
    }
    defaultLocaleEntry {
      name
      id
      specialization {
        ...MachineSpecializationData
      }
    }
    node_locale
  }
`;
